import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (productUrl?.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }

    // If variant-picker is a child of a card-level component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component') || this.closest('product-card')) {
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // On PDP, include the section ID for a faster section-only fetch instead of the full page
    const sectionId = this.dataset.sectionId || this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    if (sectionId) {
      return `${productUrl}?section_id=${sectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`${this.tagName.toLowerCase()} script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            let variantData;
            try {
              variantData = JSON.parse(textContent);
            } catch (parseError) {
              console.warn('Variant data could not be parsed; skipping variant update.', parseError);
              return;
            }

            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

/**
 * Variant card options component for PDP.
 * Resolves variants client-side without fetching — updates the add-to-cart variant ID,
 * price, and button state instantly.
 */
class VariantCardOptionsComponent extends VariantPicker {
  /** @type {Array<{id: number, available: boolean, price: number, compare_at_price: number, options: string[], featured_media?: object}> | null} */
  #variants = null;

  /** @type {string[] | null} */
  #optionNames = null;

  connectedCallback() {
    super.connectedCallback();
    const variantsScript = this.querySelector('script[data-variants]');
    const optionNamesScript = this.querySelector('script[data-option-names]');
    try {
      if (variantsScript) this.#variants = JSON.parse(variantsScript.textContent);
      if (optionNamesScript) this.#optionNames = JSON.parse(optionNamesScript.textContent);
    } catch (parseError) {
      console.warn('Variant card options data could not be parsed; client-side variant resolution disabled.', parseError);
      this.#variants = null;
      this.#optionNames = null;
    }
  }

  /**
   * Handles the variant change event — resolves the variant client-side, no fetch.
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption = event.target;
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // Shopify already resolves the exact variant for this option value (given the other options'
    // current selection) at render time — prefer that over re-deriving it client-side, and only fall
    // back to the heuristic resolver if that data isn't available (e.g. ambiguous 3+ option products).
    const renderedVariantId = selectedOption.dataset.variantId ? Number(selectedOption.dataset.variantId) : null;
    const renderedVariant =
      renderedVariantId != null ? this.#variants?.find((v) => v.id === renderedVariantId) : undefined;

    const variant = renderedVariant ?? this.#resolveVariant();
    if (!variant) return;

    // Build a minimal HTML document so VariantUpdateEvent consumers can query it
    const html = this.#buildPriceHtml(variant);

    this.dispatchEvent(
      new VariantUpdateEvent(variant, selectedOption.dataset.optionValueId ?? '', {
        html,
        productId: this.dataset.productId ?? '',
      })
    );

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    if (url.href !== window.location.href) {
      requestYieldCallback(() => history.replaceState({}, '', url.toString()));
    }
  }

  /**
   * Resolves the current variant from the options selected in this component, falling back to the
   * currently active variant (tracked by the add-to-cart form's hidden variant id input) for any
   * option this component doesn't render itself — e.g. Color, when it's driven by a bespoke swatch
   * block rather than a sibling `<variant-picker>` element.
   * @returns {object | undefined}
   */
  #resolveVariant() {
    if (!this.#variants || !this.#optionNames) return undefined;

    const section = this.closest('.shopify-section');
    const currentVariantInput = /** @type {HTMLInputElement | null} */ (
      section?.querySelector('input[name="id"][ref="variantId"]')
    );
    const currentVariantId = currentVariantInput ? Number(currentVariantInput.value) : null;
    const currentVariant =
      currentVariantId != null ? this.#variants.find((v) => v.id === currentVariantId) : undefined;

    const selectedOptions = this.#optionNames.map((optionName, index) => {
      const namePrefix = `${CSS.escape(optionName)}-`;

      const ownChecked = this.querySelector(`fieldset input[name^="${namePrefix}"]:checked`);
      if (ownChecked instanceof HTMLInputElement) return ownChecked.value;

      return currentVariant?.options[index] ?? null;
    });

    const matches = (v) => v.options.every((opt, i) => selectedOptions[i] == null || selectedOptions[i] === opt);

    return this.#variants.find((v) => matches(v) && v.available !== false) ?? this.#variants.find(matches);
  }

  /**
   * Builds a minimal HTML document with price markup for VariantUpdateEvent consumers.
   * @param {object} variant
   * @returns {Document}
   */
  #buildPriceHtml(variant) {
    const price = this.#formatPrice(variant.price);
    const compareAtPrice = variant.compare_at_price > variant.price ? this.#formatPrice(variant.compare_at_price) : '';
    const showCompare = variant.compare_at_price > variant.price;
    const discountPercent = showCompare
      ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
      : 0;

    const regularLabel = this.dataset.i18nPriceRegular || 'Regular price';
    const saleLabel = this.dataset.i18nPriceSale || 'Sale price';

    // Mirrors snippets/price.liquid with show_sale_price_first: true (this theme's default everywhere
    // it renders a price), so the client-resolved markup matches the server-rendered layout exactly.
    // The gap before the discount badge is given an explicit margin (rather than relying on
    // whitespace between tags, which doesn't survive every DOM-insertion path) so it can't be lost.
    let priceHtml = '';
    if (showCompare) {
      priceHtml += `<span role="group"><span class="visually-hidden">${saleLabel}&nbsp;</span><span class="price bold">${price}</span></span>`;
      priceHtml += `<span role="group"><span class="visually-hidden">${regularLabel}&nbsp;</span><span class="compare-at-price ml-2">${compareAtPrice}</span></span>`;
      priceHtml += `<span class="font-bold ml-2" style="background: linear-gradient(284deg, #F05223 7.5%, #F51C82 92.5%); -webkit-background-clip: text; background-clip: text; color: transparent;">-${discountPercent}%</span>`;
    } else {
      priceHtml += `<span class="price bold">${price}</span>`;
    }

    const markup = `<product-price><div ref="priceContainer" aria-live="polite" aria-atomic="true">${priceHtml}</div></product-price>`;
    return new DOMParser().parseFromString(markup, 'text/html');
  }

  /**
   * Formats a price in cents using Shopify's money format.
   * @param {number} cents
   * @returns {string}
   */
  #formatPrice(cents) {
    const moneyFormat = window.Shopify?.money_format || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');

    return moneyFormat
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }
}

if (!customElements.get('variant-card-options-component')) {
  customElements.define('variant-card-options-component', VariantCardOptionsComponent);
}
